import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";
import {
  SHEET_APPROVAL_BLOCKED_REASON,
  SHEET_APPROVAL_INELIGIBLE_REASON,
  SHEET_APPROVAL_TYPE,
  type IResolvedSheetApprovalUnit,
  type ISheetApprovalRecipient,
  type ISheetApprovalRequestResponse,
  type ISheetApprovalRequestState,
  type ISheetApprovalUnit,
  type SheetApprovalType,
} from "./types";

/**
 * What the header control should do.
 *
 * `hidden` — this business has no approval-enabled sheets, so the whole concept
 * is off. A greyed-out button would read as a hint that something is missing.
 *
 * `unreachable` — approvals ARE configured here but nobody can be emailed. Shown
 * disabled with a reason: staying silent would look like the feature is absent
 * rather than misconfigured.
 */
export const SHEET_APPROVAL_ACTION_MODE = {
  hidden: "hidden",
  unreachable: "unreachable",
  ready: "ready",
} as const;

export type SheetApprovalActionMode =
  (typeof SHEET_APPROVAL_ACTION_MODE)[keyof typeof SHEET_APPROVAL_ACTION_MODE];

export const countEligibleRecipients = (
  state: ISheetApprovalRequestState | undefined,
): number =>
  state?.recipients.filter((recipient) => recipient.eligible).length ?? 0;

export const getSheetApprovalActionMode = (
  state: ISheetApprovalRequestState | undefined,
): SheetApprovalActionMode => {
  if (!state || state.approval_units.length === 0) {
    return SHEET_APPROVAL_ACTION_MODE.hidden;
  }
  if (!state.can_send || countEligibleRecipients(state) === 0) {
    return SHEET_APPROVAL_ACTION_MODE.unreachable;
  }
  return SHEET_APPROVAL_ACTION_MODE.ready;
};

/**
 * Why the control is disabled, in the manager's terms.
 *
 * Mapped per reason rather than assuming "nobody can be emailed": pointing someone
 * at workbook access settings is the wrong instruction when the problem is that the
 * run has no workbook, and the reason list will grow.
 */
export const getSheetApprovalUnavailableMessage = (
  state: ISheetApprovalRequestState | undefined,
): string => {
  switch (state?.cannot_send_reason) {
    case SHEET_APPROVAL_BLOCKED_REASON.generatedWorkbookNotFound:
      return "This payroll run has no workbook yet, so there is nothing to approve.";
    case SHEET_APPROVAL_BLOCKED_REASON.noApprovers:
      return "No approver on these sheets can be emailed. Check workbook access settings.";
    case SHEET_APPROVAL_BLOCKED_REASON.noApprovableSheets:
      return "No sheet in this workbook is set up to need approval.";
    default:
      return "Approval cannot be requested for this run right now.";
  }
};

/** Human-readable reason an approver could not be emailed. */
export const getIneligibleExplanation = (
  recipient: ISheetApprovalRecipient,
): string => {
  switch (recipient.ineligible_reason) {
    case SHEET_APPROVAL_INELIGIBLE_REASON.missingEmail:
      return "no email address on file";
    case SHEET_APPROVAL_INELIGIBLE_REASON.technicianCannotAccessWorkbook:
      return "a technician account, which cannot open the workbook";
    default:
      return "unavailable";
  }
};

/** Approval types in the order they are shown, so the two never swap places. */
export const SHEET_APPROVAL_TYPES: SheetApprovalType[] = [
  SHEET_APPROVAL_TYPE.readyForTechApproval,
  SHEET_APPROVAL_TYPE.payrollFinalized,
];

/**
 * The units this dialog lists, each carrying the gate state the workbook payload
 * already reported for it.
 *
 * The join is on the access key, which both sides name the same thing because it
 * is the same durable unit. Done once here rather than at each caller so the
 * dialog never holds two ideas of how far a department has got.
 */
export const resolveUnitApprovals = (
  units: ISheetApprovalUnit[],
  approvalStatus: IPayrollSheetApprovalStatus | null | undefined,
): IResolvedSheetApprovalUnit[] =>
  units.map((unit) => ({
    ...unit,
    approvals:
      approvalStatus?.access_keys.find(
        (accessKey) => accessKey.key === unit.access_key,
      )?.approvals ?? null,
  }));

/**
 * Whether any unit carries a sign-off yet. On a period nobody has started
 * signing, a mark against every unit reads as "all of this is overdue", so the
 * dialog shows none at all until there is something to compare against.
 */
export const hasAnyApproval = (units: IResolvedSheetApprovalUnit[]): boolean =>
  units.some((unit) =>
    SHEET_APPROVAL_TYPES.some((type) => unit.approvals?.[type]?.approved),
  );

/** Units carrying every sign-off — nothing is outstanding on them. */
export const isFullyApproved = (unit: IResolvedSheetApprovalUnit): boolean =>
  SHEET_APPROVAL_TYPES.every((type) => unit.approvals?.[type]?.approved);

/**
 * Whether everything this approver owns is fully signed off — both approvals, on
 * every unit. The only state in which there is nothing left to ask them for.
 *
 * Deliberately NOT influenced by whether they have already been emailed. Being
 * asked is not the same as responding, and a reminder to someone who was emailed
 * but never approved is exactly what this dialog is for.
 */
export const isRecipientFullyApproved = (
  recipient: ISheetApprovalRecipient,
  units: IResolvedSheetApprovalUnit[],
): boolean => {
  const owned = getRecipientUnits(recipient, units);
  return owned.length > 0 && owned.every(isFullyApproved);
};

/** Who to tick when the dialog opens: every reachable approver not yet done. */
export const getDefaultSelectedRecipientIds = (
  state: ISheetApprovalRequestState,
  approvalStatus: IPayrollSheetApprovalStatus | null | undefined,
): string[] => {
  const units = resolveUnitApprovals(state.approval_units, approvalStatus);

  return state.recipients
    .filter(
      (recipient) =>
        recipient.eligible && !isRecipientFullyApproved(recipient, units),
    )
    .map((recipient) => recipient.user_id);
};

/** Approvers the dialog offers at all — the unreachable are not actionable here. */
export const getSelectableRecipients = (
  state: ISheetApprovalRequestState,
): ISheetApprovalRecipient[] =>
  state.recipients.filter((recipient) => recipient.eligible);

/** Units by access key, so a recipient's keys resolve to their unit + status. */
export const indexUnitsByKey = <T extends ISheetApprovalUnit>(
  units: T[],
): Record<string, T> =>
  Object.fromEntries(units.map((unit) => [unit.access_key, unit]));

/**
 * The units one recipient is being asked to approve, in workbook order. Driven by
 * `approval_units` rather than the recipient's own key list so ordering matches
 * the rest of the dialog.
 */
// Generic over the unit so a caller that resolved gate state onto it keeps that
// state through the lookup; the ordering and filtering are the same either way.
export const getRecipientUnits = <T extends ISheetApprovalUnit>(
  recipient: ISheetApprovalRecipient,
  units: T[],
): T[] => {
  const byKey = indexUnitsByKey(units);
  return recipient.access_keys
    .map((key) => byKey[key])
    .filter((unit): unit is T => Boolean(unit))
    .sort((a, b) => units.indexOf(a) - units.indexOf(b));
};

/** Confirm-button label for the current selection. */
export const getSendButtonLabel = (selectedCount: number): string => {
  if (selectedCount === 0) return "Send requests";
  return selectedCount === 1
    ? "Send 1 request"
    : `Send ${selectedCount} requests`;
};

export interface SheetApprovalSendSummary {
  /** Deliveries that were actually attempted — what the confirm button promised. */
  attempted: number;
  sent: number;
  /** Recipients the confirmation already showed as unreachable. */
  skipped: number;
  /** Deliveries that broke unexpectedly while being enqueued. */
  failed: number;
}

/**
 * Splits a send into the three outcomes that read differently to a manager.
 *
 * `skipped` is deliberately excluded from `attempted`: those recipients were
 * named in the dialog before the manager confirmed, so counting them as part of
 * the send would contradict the "Send N requests" button they just clicked.
 * Only `failed` is new information worth an error.
 */
export const summarizeApprovalSend = (
  response: ISheetApprovalRequestResponse,
): SheetApprovalSendSummary => {
  // Counted on `skipped` alone, not on `skipped && status === "failed"`. The API
  // pairs the two today, but relying on that meant a skipped delivery arriving with
  // any other status would have been counted as attempted AND sent.
  const skipped = response.deliveries.filter(
    (delivery) => delivery.skipped,
  ).length;
  const failed = response.deliveries.filter(
    (delivery) => !delivery.skipped && delivery.status === "failed",
  ).length;

  return {
    attempted: response.deliveries.length - skipped,
    sent: response.deliveries.length - skipped - failed,
    skipped,
    failed,
  };
};
